import { useEffect, useRef, useState } from "react";
import { siteData, AboutData } from "@/store/siteData";
import { useImageUrl } from "@/hooks/useImageUrl";

function VisionSection({ data, visible }: { data: AboutData; visible: boolean }) {
  const imgUrl = useImageUrl(undefined, "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=700&q=80");
  return (
    <section className="py-24 border-b border-[#E2E8F0]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className={`transition-all duration-700 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"}`}>
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-12 bg-[#3B82F6]"></div>
              <span className="text-[#334155] text-xs font-semibold tracking-[0.3em] uppercase">{data.visionTagline}</span>
            </div>
            <h2 className="text-[#0F172A] font-black uppercase leading-tight mb-6"
              style={{ fontSize: "clamp(24px, 3.5vw, 44px)", letterSpacing: "-0.02em" }}
            >
              {data.visionTitle.split("\n").map((line, i) => (
                <span key={i}>
                  {i === data.visionTitle.split("\n").length - 1
                    ? <span className="text-[#64748B]">{line}</span>
                    : <>{line}<br /></>}
                </span>
              ))}
            </h2>
            <p className="text-[#64748B] text-sm leading-relaxed mb-6">{data.visionText1}</p>
            <p className="text-[#64748B] text-sm leading-relaxed">{data.visionText2}</p>
          </div>

          <div className={`relative transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6"}`}>
            <div className="relative aspect-[4/3] overflow-hidden border border-[#E2E8F0]">
              <img src={imgUrl} alt="C2TEK Ekibi" className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#F8FAFC]/60 to-transparent"></div>
            </div>
            <div className="absolute -bottom-5 -left-5 bg-[#FFFFFF] border border-[#E2E8F0] p-5">
              <p className="text-[#0F172A] font-black text-2xl leading-tight">{data.floatStatValue}</p>
              <p className="text-[#64748B] text-xs tracking-widest uppercase">{data.floatStatLabel}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ValuesSection({ data, visible }: { data: AboutData; visible: boolean }) {
  return (
    <section className="py-24 border-b border-[#E2E8F0]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className={`mb-12 transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#3B82F6]"></div>
            <span className="text-[#334155] text-xs font-semibold tracking-[0.3em] uppercase">{data.valuesTagline}</span>
          </div>
          <h2 className="text-[#0F172A] font-black uppercase" style={{ fontSize: "clamp(24px, 3vw, 42px)", letterSpacing: "-0.02em" }}>
            {data.valuesTitle}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {data.values.map((val, i) => (
            <div key={val.id} className={`border border-[#E2E8F0] hover:border-[#3B82F6] bg-[#FFFFFF] p-6 transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{ transitionDelay: `${200 + i * 100}ms` }}>
              <div className="w-10 h-10 flex items-center justify-center border border-[#94A3B8] bg-[#F1F5F9] mb-4">
                <i className={`${val.icon} text-[#334155] text-lg`}></i>
              </div>
              <h3 className="text-[#0F172A] font-bold text-sm uppercase tracking-wide mb-2">{val.title}</h3>
              <p className="text-[#64748B] text-xs leading-relaxed">{val.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TimelineSection({ data, visible }: { data: AboutData; visible: boolean }) {
  return (
    <section className="py-24 border-b border-[#E2E8F0]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className={`mb-12 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#3B82F6]"></div>
            <span className="text-[#334155] text-xs font-semibold tracking-[0.3em] uppercase">{data.timelineTagline}</span>
          </div>
          <h2 className="text-[#0F172A] font-black uppercase" style={{ fontSize: "clamp(24px, 3vw, 42px)", letterSpacing: "-0.02em" }}>
            {data.timelineTitle}
          </h2>
        </div>
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-px bg-[#E2E8F0] ml-5"></div>
          <div className="space-y-8 pl-14">
            {data.milestones.map((m, i) => (
              <div key={m.id} className={`relative flex items-start gap-5 transition-all duration-700 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"}`} style={{ transitionDelay: `${400 + i * 100}ms` }}>
                <div className="absolute -left-9 w-3 h-3 bg-[#3B82F6] border-2 border-[#E2E8F0] rounded-full mt-1.5"></div>
                <div>
                  <span className="text-[#334155] text-sm font-black tracking-widest">{m.year}</span>
                  <p className="text-[#64748B] text-sm mt-1">{m.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TeamMemberCard({ member, visible, delay }: { member: { id: number; name: string; title: string; department: string; imageId?: string; imageUrl: string }; visible: boolean; delay: number }) {
  const imgUrl = useImageUrl(member.imageId, member.imageUrl);
  return (
    <div className={`border border-[#E2E8F0] hover:border-[#3B82F6] bg-[#FFFFFF] overflow-hidden transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{ transitionDelay: `${delay}ms` }}>
      <div className="h-56 overflow-hidden">
        <img src={imgUrl} alt={member.name} className="w-full h-full object-cover object-top" />
      </div>
      <div className="p-5">
        <p className="text-[#94A3B8] text-xs tracking-widest uppercase mb-1">{member.department}</p>
        <h3 className="text-[#0F172A] font-bold text-base mb-1">{member.name}</h3>
        <p className="text-[#64748B] text-sm">{member.title}</p>
      </div>
    </div>
  );
}

function TeamSection({ data, visible }: { data: AboutData; visible: boolean }) {
  if (!data.team || data.team.length === 0) return null;
  return (
    <section className="py-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className={`mb-12 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#3B82F6]"></div>
            <span className="text-[#334155] text-xs font-semibold tracking-[0.3em] uppercase">Ekibimiz</span>
          </div>
          <h2 className="text-[#0F172A] font-black uppercase" style={{ fontSize: "clamp(24px, 3vw, 42px)", letterSpacing: "-0.02em" }}>
            {data.teamTitle}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {data.team.map((member, i) => (
            <TeamMemberCard key={member.id} member={member} visible={visible} delay={200 + i * 100} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function AboutContent() {
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState<AboutData>(siteData.getAbout());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handler = () => setData(siteData.getAbout());
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  return (
    <div ref={ref} className="bg-[#F8FAFC]">
      <VisionSection data={data} visible={visible} />
      <ValuesSection data={data} visible={visible} />
    </div>
  );
}
